import type { CommonColors } from '@mui/material/styles';

import type { PaletteColorNoChannels } from './core/palette';
import type { ThemeDirection, ThemeColorScheme, ThemeCssVariables } from './types';

// ----------------------------------------------------------------------

type ThemeConfig = {
  classesPrefix: string;
  modeStorageKey: string;
  enableSystemMode: boolean;
  direction: ThemeDirection;
  defaultMode: ThemeColorScheme;
  cssVariables: ThemeCssVariables;
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<
    'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error',
    PaletteColorNoChannels
  > & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: Record<
      '0' | '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900',
      string
    >;
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  defaultMode: 'light',
  enableSystemMode: false,
  modeStorageKey: 'theme-mode',
  direction: 'ltr',
  classesPrefix: 'minimal',
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: 'Public Sans Variable',
    secondary: 'Barlow',
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#DFECFB',
      light: '#9CBCEB',
      main: '#5478BF',
      dark: '#2A4389',
      darker: '#101F5B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      lighter: '#D6E4FF',
      light: '#84A9FF',
      main: '#3366FF',
      dark: '#1939B7',
      darker: '#091A7A',
      contrastText: '#FFFFFF',
    },
    info: {
      lighter: '#D9FBFD',
      light: '#8EE3F8',
      main: '#43B3E8',
      dark: '#216AA7',
      darker: '#0C366F',
      contrastText: '#FFFFFF',
    },
    success: {
      lighter: '#EDFBDA',
      light: '#B8EA8D',
      main: '#68BA3F',
      dark: '#34851F',
      darker: '#11590C',
      contrastText: '#ffffff',
    },
    warning: {
      lighter: '#FEF7D6',
      light: '#FCDF84',
      main: '#F7BC33',
      dark: '#B17A19',
      darker: '#764809',
      contrastText: '#212B36',
    },
    error: {
      lighter: '#FFE8D4',
      light: '#FFA87F',
      main: '#FF4E2B',
      dark: '#B71715',
      darker: '#7A0818',
      contrastText: '#FFFFFF',
    },
    grey: {
      "0": '#FFFFFF',
      "50": '#FCFDFD',
      "100": '#F9FAFB',
      "200": '#F4F6F8',
      "300": '#DFE3E8',
      "400": '#C4CDD5',
      "500": '#919EAB',
      "600": '#637381',
      "700": '#454F5B',
      "800": '#212B36',
      "900": '#161C24',
    },
    common: { black: '#000000', white: '#FFFFFF' },
  },
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'class',
  },
};
